/**
 * Feature E04-F01 — "Inbox: список, превью, классификация" request/response
 * contracts (E04-F01-T02 / T03 / T07).
 *
 * 1:1 port of the legacy n8n Inbox group (docs/source-n8n/reference/Inbox.md →
 * "Inbox: Get Emails", action get_emails) plus the Email Checker classification
 * meta (reference/EmailChecker.md → "Filter & Classify" / "Save Emails") and the
 * branch-7 `get_dismissed` action (E04-F01-T03 spec).
 *
 * Single source of truth for:
 *   - GET /emails           list / single-email read (T02 consumer = Inbox list + preview)
 *   - GET /emails/dismissed hidden (dismissed) emails list (T03)
 *   - the `meta` shape (classification, user_id, attachments[], matched_*) (T07)
 *
 * IDOR invariant (carried over unchanged from the n8n `_common`): ownership is
 * derived from the SESSION user id, never from a body field. `user_id` is part
 * of the stored `meta` for *display/ownership* only — the route filters by the
 * authenticated session id (plus `'system'`), and no input schema here accepts a
 * `user_id`/`owner_id` so it can never be trusted from a request.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// T07 — attachment metadata (meta.attachments[]) — Email Checker layer 2
// ---------------------------------------------------------------------------
/**
 * One attachment's metadata as persisted by the Email Checker
 * (reference/EmailChecker.md → "Filter & Classify": attachments mapped to
 * `{ filename, contentType, size }`; the base64 content is NOT stored in meta).
 */
export const emailAttachmentSchema = z.object({
    filename: z.string(),
    /** MIME type, e.g. `application/pdf`. Used by the UI to pick an icon. */
    contentType: z.string().optional(),
    /** Size in bytes (the UI renders KB / MB). */
    size: z.number().optional(),
});
// ---------------------------------------------------------------------------
// classification literals (Email Checker: rule-based, no LLM)
// ---------------------------------------------------------------------------
/**
 * The `meta->>'classification'` values the Email Checker writes
 * (reference/EmailChecker.md → "Filter & Classify"):
 *   - 'unclassified'           default; sender not matched to a contact/client
 *   - 'new_project_candidate'  subject/sender matched the new-project regex/domain
 *   - 'existing_project'       set by link_to_project (the email was linked)
 *   - 'sent'                   set on outgoing replies (direction='out')
 * The column is free-form text, so the schema accepts any string and the UI
 * humanizes the known ones with a graceful fallback.
 */
export const EMAIL_CLASSIFICATIONS = [
    'unclassified',
    'new_project_candidate',
    'existing_project',
    'sent',
];
/** Free-form classification literal (the column is text). */
export const emailClassificationSchema = z.string();
// ---------------------------------------------------------------------------
// T07 — email meta (jsonb) shape
// ---------------------------------------------------------------------------
/**
 * The `emails.meta` jsonb payload (Email Checker "Filter & Classify" output).
 * Free-form by nature (jsonb), so the schema is permissive and passes through
 * any extra keys, but documents the fields the Inbox UI and routes read.
 */
export const emailMetaSchema = z
    .object({
    classification: emailClassificationSchema.optional(),
    /** Owning recruiter session id, or the literal `'system'`. */
    user_id: z.string().optional(),
    /** Mailbox account that received the email (id + address). */
    account_id: z.union([z.number(), z.string()]).optional(),
    account_email: z.string().optional(),
    /** Info-only matches (never auto-linked — see EmailChecker.md invariant). */
    matched_contact_id: z.union([z.number(), z.string()]).nullable().optional(),
    matched_client_id: z.union([z.number(), z.string()]).nullable().optional(),
    matched_project_id: z.union([z.number(), z.string()]).nullable().optional(),
    /** Hidden-from-inbox flag (branch-7 dismiss). */
    dismissed: z.boolean().optional(),
    /** Attachment metadata (no base64 content). */
    attachments: z.array(emailAttachmentSchema).optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// T02 — email DTO (get_emails row shape)
// ---------------------------------------------------------------------------
/**
 * One row of the `get_emails` SELECT (reference/Inbox.md → "Inbox: Get Emails"):
 *
 *   SELECT e.id, sender, sender_name, recipient, subject,
 *          LEFT(e.body,200) AS body_preview, [e.body],
 *          direction, thread_id, external_id, received_at, project_id, meta,
 *          p.role_name AS project_role, cl.name AS client_name
 *   FROM emails e
 *   LEFT JOIN projects p ON e.project_id = p.id
 *   LEFT JOIN clients  cl ON p.client_id = cl.id
 *
 * `body` is present ONLY for a single-email read (`email_id`); list rows carry
 * `body_preview` (LEFT 200) instead — the legacy traffic-saving optimization.
 */
export const emailSchema = z.object({
    id: z.number().int(),
    sender: z.string().nullable(),
    sender_name: z.string().nullable(),
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    /** LEFT(body, 200) — present on every list row. */
    body_preview: z.string().nullable(),
    /** Full body — present ONLY on a single-email (`email_id`) read. */
    body: z.string().nullable().optional(),
    direction: z.string().nullable(),
    thread_id: z.string().nullable(),
    external_id: z.string().nullable(),
    /** ISO 8601 timestamp. */
    received_at: z.string().nullable(),
    project_id: z.number().int().nullable(),
    meta: emailMetaSchema,
    /** From the LEFT JOIN projects (null when unlinked). */
    project_role: z.string().nullable(),
    /** From the LEFT JOIN clients via the project (null when unlinked). */
    client_name: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// T02 — GET /emails query params + response
// ---------------------------------------------------------------------------
/** Default page size (legacy `EMAILS_PAGE_SIZE = 50`). */
export const EMAILS_PAGE_SIZE = 50;
/**
 * GET /emails query string. All optional (the route applies the legacy
 * defaults). NOTE there is intentionally NO `user_id` — ownership is the session
 * id only (IDOR rule). `classification` is whitelisted/parameterized server-side
 * (legacy interpolated it raw — a SQL-injection hole we close here).
 */
export const getEmailsQuerySchema = z.object({
    /** Single-email read: returns one row WITH the full `body`. */
    email_id: z.coerce.number().int().positive().optional(),
    /** Filter to a single project's emails. */
    project_id: z.coerce.number().int().positive().optional(),
    /**
     * Classification filter. `'all'` / absent → no filter. `'existing_project'`
     * → `project_id IS NOT NULL` (special case, NOT a meta match). Any other
     * value → `meta->>'classification' = value`.
     */
    classification: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * GET /emails response.
 *
 * Legacy parity NOTE: the n8n node double-serialized `emails` as a JSON *string*
 * inside the envelope (`{ success:true, emails: "<json array>" }`). The v2
 * contract returns a real array (the legacy double-serialization was flagged as
 * a wart in reference/Inbox.md — "при переписывании решить"). The web client
 * already parses both shapes via `normalizeN8nPayload`.
 */
export const getEmailsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    emails: z.array(emailSchema),
});
// ---------------------------------------------------------------------------
// T03 — GET /emails/dismissed response
// ---------------------------------------------------------------------------
/** Hard cap matching the legacy branch-7 `get_dismissed` LIMIT 100. */
export const DISMISSED_EMAILS_LIMIT = 100;
/**
 * One row of the dismissed list (branch-7 `get_dismissed`):
 *   id, sender, recipient, subject, LEFT(body,300) AS body_preview,
 *   received_at, meta, direction — project_id IS NULL AND meta.dismissed = true.
 * A subset of {@link emailSchema}; the body preview is 300 chars here (vs 200).
 */
export const dismissedEmailSchema = z.object({
    id: z.number().int(),
    sender: z.string().nullable(),
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    /** LEFT(body, 300). */
    body_preview: z.string().nullable(),
    received_at: z.string().nullable(),
    meta: emailMetaSchema,
    direction: z.string().nullable(),
});
/** GET /emails/dismissed response. */
export const getDismissedEmailsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    emails: z.array(dismissedEmailSchema),
});
//# sourceMappingURL=inbox.js.map